import { Error, LinkList, Loader } from '@unmind/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import RoutePath from 'App/RoutePath';
import { tracking } from 'App/Tracking';
import {
  LocationModal,
  SessionAllowance,
  UpcomingAppointments,
  useGetUpcomingCoachingAndTherapyAppointments,
  useCoachingAndTherapyUserSessionLimits,
  useGetUpcomingEAPAppointments,
} from 'Book/shared';
import { getChannel } from 'utils';

import { Section } from './Book.styles';
import { EAPServices, HeroBanner, Practitioners } from './components';
import { useBookAccess } from './hooks';

export default () => {
  const { t } = useTranslation('book', { keyPrefix: 'book' });
  const { supportUrl } = getChannel();
  const { remainingSessions, sessionLimit, sessionsExpired } =
    useCoachingAndTherapyUserSessionLimits();
  const {
    loading: loadingBookableServices,
    canAccess,
    entitlements,
    bookableServices,
  } = useBookAccess();
  const {
    loading: loadingWpoAppointments,
    error: errorWpo,
    data,
  } = useGetUpcomingEAPAppointments();

  const {
    loading: loadingTherapyAppointments,
    error: errorTherapy,
    data: { upcomingEvents, upcomingEventRequests, userID },
  } = useGetUpcomingCoachingAndTherapyAppointments();

  if (
    loadingBookableServices ||
    loadingWpoAppointments ||
    loadingTherapyAppointments
  ) {
    return (
      <Section className="wrapper">
        <Loader />
      </Section>
    );
  }

  if (!canAccess) {
    return (
      <Section className="wrapper">
        <Error errorText={t('wpo_services.error')} />
      </Section>
    );
  }

  const hasCounselling = bookableServices?.some(
    service => service.serviceGroup === 'counselling',
  );
  const hasCounsellingAppointment = data?.upcomingAppointments?.some(
    appointment => appointment.service?.serviceGroup === 'counselling',
  );
  const hasTalk = entitlements.unmindTalk;

  const upcomingTherapyAppointments = [];
  if (upcomingEvents) {
    upcomingTherapyAppointments.push(...upcomingEvents);
  }
  if (upcomingEventRequests) {
    upcomingTherapyAppointments.push(...upcomingEventRequests);
  }

  const upcomingAppointments = !errorWpo && !errorTherapy && (
    <UpcomingAppointments
      bookableServices={bookableServices}
      upcomingEapAppointments={data?.upcomingAppointments ?? []}
      upcomingTherapyAppointments={upcomingTherapyAppointments}
      userID={userID}
      fallback={<Practitioners />}
    />
  );

  if (bookableServices.length === 0 && upcomingTherapyAppointments.length > 0) {
    return (
      <Section
        className="wrapper"
        data-testid="coaching-and-therapy-appointments-page"
      >
        <SessionAllowance />
        <main>
          {upcomingAppointments}
          <LinkList
            links={[
              {
                label: t('links.browse'),
                href: RoutePath.BookPractitioners,
                onClick: () => {
                  tracking.track('book_link_clicked', { text: 'browse' });
                },
              },
              {
                label: t('links.helpCentre'),
                href: supportUrl,
                onClick: () => {
                  tracking.track('book_link_clicked', { text: 'help_centre' });
                },
              },
            ]}
          />
        </main>
      </Section>
    );
  }

  return (
    <Section className="wrapper" data-testid="book-page">
      <HeroBanner
        entitlements={{
          hasCounselling: hasCounselling,
          hasCounsellingAppointment: hasCounsellingAppointment ?? false,
          hasTalk: hasTalk,
          hasMatching: entitlements.talkAiMatching,
        }}
        sessions={{ remainingSessions, sessionLimit, sessionsExpired }}
      />

      {(data?.upcomingAppointments && data?.upcomingAppointments?.length > 0) ||
      upcomingTherapyAppointments.length > 0
        ? upcomingAppointments
        : hasTalk && <Practitioners />}

      <EAPServices bookableServices={bookableServices} />

      {hasTalk && <LocationModal />}
    </Section>
  );
};
